import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

const Stars = ({ rating, size = 'w-4 h-4' }) => (
  <div className="flex space-x-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} className={`${size} ${rating >= n ? 'fill-amber-400 text-amber-400' : 'text-border'}`} />
    ))}
  </div>
);

const ReviewsSection = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  if (!data || data.count === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-muted/30" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What Our Customers Say
          </h2>
          <div className="flex items-center justify-center space-x-3" data-testid="reviews-average">
            <Stars rating={Math.round(data.average)} size="w-6 h-6" />
            <span className="text-lg font-semibold text-foreground">{data.average.toFixed(1)}</span>
            <span className="text-foreground/60">from {data.count} review{data.count !== 1 ? 's' : ''}</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.reviews.slice(0, 6).map((review, index) => (
            <motion.div
              key={review.review_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-8 rounded-2xl bg-background/50 border border-border hover:border-primary/30 transition-all duration-300 flex flex-col"
              data-testid={`review-card-${index}`}
            >
              <Stars rating={review.rating} />
              <p className="text-foreground/80 leading-relaxed mt-4 flex-1">"{review.text}"</p>
              <div className="mt-6 pt-4 border-t border-border">
                <p className="font-semibold text-foreground text-sm">
                  {review.name}
                  {(review.suburb || review.state) && (
                    <span className="text-foreground/50 font-normal"> — {[review.suburb, review.state].filter(Boolean).join(', ')}</span>
                  )}
                </p>
                {review.product && <p className="text-xs text-primary mt-1">{review.product}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
